import type { RoutineTimelineData, TimelineReport, TimelineRoute } from '../types/RoutineTimeline.types';
import { ROUTINE_TIMELINE_PAGE_SIZE } from './constants';
import { parseDurationMs } from './duration';
import type { RobotCleaningControlService } from 'app/modules/machine-inventory/RobotCleaningControlService';
import { getCurrentCleaningTask } from 'app/modules/machine-inventory/utils/robotCleaningUtils';
import type { Optional } from 'lib/types/Optional';

/** The cleaning task report id of a route that finished at `endTime` (epoch ms), or null without a usable one. */
export const getCleaningTaskReportId = (endTime: Optional<string>): Optional<string> => {
  const finishedAtMs = endTime ? Date.parse(endTime) : NaN;

  return Number.isNaN(finishedAtMs) ? null : String(finishedAtMs);
};

/** The finished routes of a routine run, by the run's task id. */
export const getRoutineReports = async (
  robotCleaningControlService: RobotCleaningControlService,
  materialNumber: string,
  serialNumber: string,
  taskId: string
): Promise<TimelineReport[]> => {
  const data = await robotCleaningControlService.listRoutineReports({
    materialNumber,
    serialNumber,
    cleaningGroupId: taskId,
    filter: { limit: ROUTINE_TIMELINE_PAGE_SIZE },
  });

  return (data?.cleaningReports?.nodes ?? []).flatMap(report =>
    report.routeIndex == null
      ? []
      : [
          {
            id: report.id,
            exportId: getCleaningTaskReportId(report.endTime),
            endTime: report.endTime,
            routeIndex: report.routeIndex,
            status: report.status,
            pathCoverage: report.general.pathCoverage,
            cleanedTime: report.general.cleanedTime,
          },
        ]
  );
};

/**
 * The current routine run and what its timeline needs: the routine, the routes by the robot's route number,
 * and the finished routes' reports. Null when the current task is not a routine run.
 *
 * The task's `groupId` is the robot's routine number, not the routine UUID, so the routine and its routes are
 * looked up by those numbers (with the device) — only the ones the run needs are fetched. A failing reports
 * query only leaves the finished rows without numbers, and a route that cannot be read only loses its name and
 * duration.
 */
export const getRoutineTimelineData = async (
  robotCleaningControlService: RobotCleaningControlService,
  materialNumber: string,
  serialNumber: string
): Promise<RoutineTimelineData | null> => {
  const task = await getCurrentCleaningTask(robotCleaningControlService, materialNumber, serialNumber);

  if (!task || !task.groupId || (task.stepCount ?? 0) <= 0) {
    return null;
  }

  const [routineData, reports] = await Promise.all([
    robotCleaningControlService.getRoutine({
      robotRoutineKey: { materialNumber, serialNumber, robotRoutineId: task.groupId },
    }),
    getRoutineReports(robotCleaningControlService, materialNumber, serialNumber, task.id).catch(error => {
      console.error(error);

      return [];
    }),
  ]);

  const routineNode = routineData?.routine;

  if (!routineNode) {
    throw new Error(`Routine ${task.groupId} not found`);
  }

  const routeNodes = await Promise.all(
    [...new Set(routineNode.routeIds)].map(robotRouteId =>
      robotCleaningControlService
        .getRoute({ robotRouteKey: { materialNumber, serialNumber, robotRouteId: String(robotRouteId) } })
        .then(data => data?.route)
        .catch(error => {
          console.error(error);

          return undefined;
        })
    )
  );

  const routes: Record<number, TimelineRoute> = {};

  routeNodes.forEach(node => {
    if (node) {
      routes[node.robotRouteId] = { name: node.name, durationMs: parseDurationMs(node.duration) };
    }
  });

  return {
    taskId: task.id,
    routine: {
      id: routineNode.id,
      name: routineNode.name,
      routeIds: routineNode.routeIds,
      durationMs: parseDurationMs(routineNode.duration),
      postChargingDurationMs: parseDurationMs(routineNode.postChargingDuration),
    },
    routes,
    reports,
  };
};
